import React, { Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faThLarge,
  faTimes,
  faSignOutAlt,
  faWalking,
  faPaw,
  faUsers,
  faList,
  faCog,
  faTaxi
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { routes } from "../../config/utility";

const Sidebar = ({ isOpen, toggleSidebar, user, roles, setShowLogoutModal }) => {
  const location = useLocation();

  const userMenuItems = [
    {
      title: "Main",
      items: [{ icon: faThLarge, label: "Dashboard", path: routes.dashboard, allowedRoles: ["admin", "user"] }],
    },
    {
      title: "Services",
      items: [
        { icon: faWalking, label: "Book Walk", path: routes.walk, allowedRoles: ["user"] },
        { icon: faTaxi, label: "Book Taxi", path: routes.delivery, allowedRoles: ["user"] },
        { icon: faPaw, label: "My Pets", path: routes.pet, allowedRoles: ["user"] },
      ],
    },
  ];

  const adminMenuItems = [
    {
      title: "Main",
      items: [{ icon: faThLarge, label: "Dashboard", path: routes.dashboard, allowedRoles: ["admin", "user"] }],
    },
    {
      title: "Orders",
      items: [
        { icon: faWalking, label: "Walk", path: routes.walk, allowedRoles: ["admin"] },
        { icon: faTaxi, label: "Taxi", path: routes.delivery, allowedRoles: ["admin"] },
      ],
    },
    {
      title: "Management",
      items: [
        { icon: faPaw, label: "Pets", path: routes.pet, allowedRoles: ["admin"] },
        { icon: faList, label: "Review", path: routes.review, allowedRoles: ["admin"] },
        { icon: faUsers, label: "Users", path: routes.user, allowedRoles: ["admin"] },
      ],
    },
    {
      title: "System",
      items: [{ icon: faCog, label: "Settings", path: routes.setting, allowedRoles: ["admin"] }],
    },
  ];

  const menuItems = roles.includes("admin") ? adminMenuItems : userMenuItems;

  return (
    <>


      {/* ✅ Desktop Sidebar (Always visible) */}
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: isOpen ? 260 : 70 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className="hidden  relative sm:flex flex-col items-center py-6 space-y-8 shadow-lg rounded-[20px] mx-2 my-5 h-[calc(100vh-40px)] bg-gradient-bg"
      >
        {/* Sidebar Toggle Button */}
        <motion.button
          onClick={toggleSidebar}
          className="absolute top-8 left-6 z-10 bg-gradient-to-br from-white to-transparent p-1 px-3 rounded-full shadow-lg text-primary hover:scale-110 hover:shadow-2xl transition-all duration-300 ease-in-out focus:outline-none"
          whileTap={{ scale: 0.9 }}
        >
          <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
        </motion.button>

        {/* Logo Section */}
        <motion.div
          className={` flex items-center justify-center mb-14 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
            }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: isOpen ? 1 : 0 }}
        >
          <img src="/assets/paw-logo.png" alt="Pawdash Logo" className="w-12 h-12 " />
        </motion.div>

        {/* Menu Items */}
        <ul className="space-y-6 relative px-2 overflow-y-scroll overflow-x-visible scrollbar-none">
          {menuItems.map((section, sectionIndex) => (
            <Fragment key={sectionIndex}>
              <h4
                className={`text-xs font-semibold text-bodyMain uppercase ${isOpen ? "" : "text-center"
                  } ${sectionIndex === 0 ? "pt-0" : "pt-4"}`}
              >
                {isOpen ? section.title : "•"}
              </h4>

              {section.items.map((item, index) => (
                <motion.li
                  key={index}
                  className="relative group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center space-x-8 ml-2 text-left text-sm tracking-wide font-medium relative group ${isActive ? "text-white bg-gradient-to-r from-primary to-transparent rounded-lg" : "text-white hover:text-white transition duration-300"
                      }`
                    }
                  >
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="flex items-center justify-center text-lg p-2 rounded-lg"
                    >
                      {location.pathname === item.path && (
                        <motion.div
                          layoutId="activeIndicator"
                          className="absolute -left-2 top-0 h-full w-1 bg-gradient-to-br from-white to-white rounded-r-lg z-10"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.4 }}
                        />
                      )}
                      <FontAwesomeIcon icon={item.icon} />
                    </motion.div>

                    {isOpen && <span className="group-hover:translate-x-1 transition-transform duration-300">{item.label}</span>}
                  </NavLink>
                </motion.li>
              ))}
            </Fragment>
          ))}
        </ul>

        <div className="flex-grow"></div>

        {/* Sign Out Button */}
        <motion.div
          onClick={() => setShowLogoutModal(true)}
          whileHover={{ scale: 1.1 }}
          className="text-[20px] text-white hover:bg-white hover:text-primary p-4 rounded-lg transition duration-200 w-[30px] h-[30px] flex justify-center items-center cursor-pointer"
        >
          <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
        </motion.div>
      </motion.div>


      {/* Mobile Sidebar */}
      <motion.div
        initial={{ x: "-100%" }}
        animate={{ x: isOpen ? "0%" : "-100%" }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed left-0 top-0 w-64 ml-2 mt-2 h-[calc(100vh-13vh)] bg-gradient-to-br from-primary to-gray-900  text-white shadow-xl z-50 sm:hidden flex flex-col rounded-[20px] "
      >
        {/* Header with Logo and Close Button */}
        <div className="flex items-center justify-between p-4  backdrop-blur-sm rounded-t-[20px]">
          <div className="flex items-center space-x-3">
            <img src="/assets/paw-logo.png" alt="Pawdash Logo" className="w-8 h-8" />
            <h1 className="text-lg font-bold text-white">PawDash</h1>
          </div>
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Menu Sections */}
        <div className="flex-1 overflow-y-auto py-4">
          {menuItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-6">
              {/* Section Title */}
              <h4 className="text-xs font-semibold text-bodyMain uppercase px-4 mb-2">
                {section.title}
              </h4>

              {/* Section Items */}
              <div className="space-y-1 ml-4">
                {section.items.map((item, index) => (
                  <NavLink
                    key={index}
                    to={item.path}
                    onClick={toggleSidebar}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-4 py-3 text-sm font-medium transition-all
                      ${isActive
                        ? 'bg-gradient-to-r from-transparent to-ash sm:from-primary  sm:to-transparent text-white  '
                        : 'text-gray-200 hover:bg-white/5'
                      }`
                    }
                  >
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className={`text-lg ${location.pathname === item.path ? 'text-white' : 'text-gray-300'}`}
                      />
                    </motion.div>
                    <span className="transform transition-transform duration-200 hover:translate-x-1">
                      {item.label}
                    </span>
                  
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer with Logout */}
        <div className="p-4 backdrop-blur-sm rounded-b-[20px]">
          <button
            onClick={() => setShowLogoutModal(true)}
            className="w-full flex items-center justify-center space-x-2 p-3 
              bg-gradient-to-r from-red-500 to-red-600 
              hover:from-red-600 hover:to-red-700
              rounded-lg transition-all duration-300
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="text-lg" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </motion.div>
  
    </>
  );
};

export default Sidebar;